import { useCallback, useContext, useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { inputStyles } from '@/styles/input';
import { CheckoutContext } from '@/context/CheckoutContext';
import { SkeletonLoader } from '../Common/SkeletonLoader';
import { Input } from '../Common/Input';
import { Email } from '../Common/Email';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { useAppchargeCard, type FieldMetadata } from '@/hooks/useAppchargeCard';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useTranslate } from '@/hooks/useTranslate';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { isEmailValid } from '@/utils/email';
import { ValidationRules } from '@/utils/validation';

export const AppchargePaymentElementForm = () => {
  const {
    customer,
    featureFlags,
    actions: { setIsFormValid },
    card: { initialCardPayload, setInitialCardPayload },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();

  const { isLoading, isCardFormValid, cardNumberMetadata, expiryMetadata, cvvMetadata } = useAppchargeCard();

  const [isZipCodeValid, setIsZipCodeValid] = useState(false);

  const {
    values,
    errors,
    touched,
    setValue,
    setFieldTouched,
    isValid: isCardholderNameValid,
  } = useFormValidation(
    { cardholderName: initialCardPayload?.cardholderName ?? '' },
    {
      cardholderName: [
        ValidationRules.required(t('cardholderName'), t('fieldErrors.incomplete', { field: t('cardholderName') })),
        ValidationRules.cardholderName(t('fieldErrors.incompleteCardholderName')),
      ],
    }
  );

  useEffect(() => {
    setIsFormValid(
      isCardFormValid && !isLoading && isZipCodeValid && isCardholderNameValid && isEmailValid(customer?.email)
    );
  }, [isCardFormValid, isLoading, isZipCodeValid, isCardholderNameValid, customer?.email, setIsFormValid]);

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  const handleCardholderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue('cardholderName', value);
    setInitialCardPayload({ ...initialCardPayload, cardholderName: value });
  };

  return (
    <div className="flex flex-col gap-2">
      {!shouldHideEmail(featureFlags) && (
        <div className="w-full">
          <Email />
        </div>
      )}

      <Input
        type="text"
        placeholder={t('cardholderName')}
        value={values.cardholderName}
        onChange={handleCardholderNameChange}
        onBlur={() => setFieldTouched('cardholderName')}
        error={touched.cardholderName ? errors.cardholderName : undefined}
        className="w-full h-11 text-base"
      />

      <div className="flex flex-col gap-4 w-full relative">
        {isLoading && <AppchargePaymentElementFormSkeleton />}

        <div className={cn('flex flex-col gap-2', isLoading && 'invisible')}>
          <div className="flex flex-col gap-1">
            <ACCardElementField metadata={cardNumberMetadata} />
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex flex-col gap-1 flex-1">
              <ACCardElementField metadata={expiryMetadata} />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <ACCardElementField metadata={cvvMetadata} />
            </div>
          </div>
        </div>
      </div>

      <InputTaxComponent onValidate={handleTaxValidationChange} />
    </div>
  );
};

interface AppchargePaymentElementFieldProps {
  metadata: FieldMetadata;
}

export function ACCardElementField({ metadata }: AppchargePaymentElementFieldProps) {
  return (
    <>
      <div
        ref={metadata.ref}
        className={cn(
          inputStyles,
          'w-full h-11 p-0 overflow-hidden',
          metadata.error && 'border-danger',
          !metadata.error &&
            '[&.ac-field--focused]:border-muted-foreground [&.ac-field--focused]:active:border-muted-foreground'
        )}
      />
      {metadata.error && <div className="text-xs text-red-500">{metadata.error}</div>}
    </>
  );
}

function AppchargePaymentElementFormSkeleton() {
  return (
    <div className="flex flex-col gap-4 w-full absolute inset-0 z-10">
      <SkeletonLoader height="h-11" width="w-full" />
      <div className="flex gap-4 w-full">
        <SkeletonLoader height="h-11" width="w-1/2" />
        <SkeletonLoader height="h-11" width="w-1/2" />
      </div>
    </div>
  );
}
